/**
 * Service for managing Daily Time Records in Firestore
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  getDocs,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { formatDateString } from '../utils/timeCalculations';
import { manilaYMD } from '../utils/periodDate';
import { filterEmployeesByDate } from '../utils/employeeFilters';

/**
 * Generate a sequential record ID for a given date
 * @param {string} companyId - Company ID
 * @param {string} employeeId - Employee ID (4-digit)
 * @param {Date} date - Date of the record
 * @returns {Promise<string>} Generated record ID
 */
export const generateRecordId = async (companyId, employeeId, date) => {
  // Keyed on the Manila date of the time-in instant (C3), like the record's `date`.
  const ymd = manilaYMD(date);
  const monthId = ymd.slice(0, 7);
  const docId = `${monthId}_${employeeId}`;
  const dateStr = ymd.replace(/-/g, '');

  // Get existing document
  const docRef = doc(db, 'companies', companyId, 'dailyTimeRecords', docId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return `rec_${dateStr}_001`;
  }

  // Highest existing suffix for this date + 1 — NOT the count: after a delete,
  // a count re-issues an id still in use (PRD_InAppNotifications C14; mirrors
  // the kiosk's nextRecordId in functions/src/kiosk/dtrCombination.js).
  const existingRecords = docSnap.data().timeRecords || [];
  const prefix = `rec_${dateStr}_`;
  let maxSequence = 0;
  existingRecords.forEach(r => {
    if (r && typeof r.id === 'string' && r.id.startsWith(prefix)) {
      const n = parseInt(r.id.slice(prefix.length), 10);
      if (Number.isFinite(n) && n > maxSequence) maxSequence = n;
    }
  });
  const nextSequence = (maxSequence + 1).toString().padStart(3, '0');

  return `${prefix}${nextSequence}`;
};

/**
 * Check for existing time records for an employee on a specific date
 * @param {string} companyId - Company ID
 * @param {string} employeeId - Employee ID
 * @param {Date} date - Date to check
 * @returns {Promise<object|null>} Existing document data or null
 */
export const checkExistingRecord = async (companyId, employeeId, date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const monthId = `${year}-${month}`;
  const docId = `${monthId}_${employeeId}`;

  const docRef = doc(db, 'companies', companyId, 'dailyTimeRecords', docId);
  const docSnap = await getDoc(docRef);

  return docSnap.exists() ? docSnap.data() : null;
};

/**
 * Save a time record to Firestore
 * @param {string} companyId - Company ID
 * @param {object} recordData - Record data including employee info and time details
 * @returns {Promise<void>}
 */
export const saveTimeRecord = async (companyId, recordData) => {
  const {
    employeeId,
    employeeDocId,
    timeIn,
    timeOut,
    breakMinutes,
    notes,
    hoursWorked,
    regularHours,
    overtimeHours,
    nightDiffHours,
    undertimeHours,
    onLeave,
    isPaidLeave,
    isAbsent,
    isPieceRate,
    pieceRateValue,
    isRestDay,
    scheduleApplied,
    policyApplied,
    lateHours,
    isLate,
    otApproval
  } = recordData;

  // The Manila date of the time-in instant keys the month doc, the `year` field and
  // the record's `date` (C3) — browser-local components misdate an entry made from a
  // zone ahead of Manila late in the Manila evening.
  const timeInYMD = manilaYMD(timeIn);
  const year = Number(timeInYMD.slice(0, 4));
  const monthId = timeInYMD.slice(0, 7);
  const docId = `${monthId}_${employeeId}`;

  const recordId = await generateRecordId(companyId, employeeId, timeIn);

  const newRecord = {
    id: recordId,
    employeeId: employeeId,
    date: timeInYMD,

    // Time tracking
    timeIn: Timestamp.fromDate(timeIn),
    timeOut: timeOut ? Timestamp.fromDate(timeOut) : null,
    breakMinutes: breakMinutes || 0,

    // Computed values
    hoursWorked: hoursWorked || 0,
    regularHours: regularHours || 0,
    overtimeHours: overtimeHours || 0,
    nightDiffHours: nightDiffHours || 0,
    undertimeHours: undertimeHours || 0,
    lateHours: lateHours || 0,
    isLate: isLate || false,

    // Source tracking
    timeInSource: onLeave === "yes" ? 'leave' : (isAbsent ? 'absent' : 'manual'),
    timeOutSource: (onLeave === "yes" || isAbsent) ? null : (timeOut ? 'manual' : null),

    // Leave tracking
    onLeave: onLeave || "no",
    isPaidLeave: isPaidLeave !== undefined ? isPaidLeave : true,

    // Absent tracking
    isAbsent: isAbsent || false,

    // Piece rate tracking
    isPieceRate: isPieceRate || false,
    pieceRateValue: pieceRateValue || null,

    // Audit trail
    createdBy: recordData.createdBy || auth.currentUser?.uid || 'system',
    createdAt: Timestamp.now(),

    // Schedule snapshot
    isRestDay: isRestDay || false,
    scheduleApplied: scheduleApplied || null,

    // Policy snapshot
    policyApplied: policyApplied || null,

    // OT Approval (only present when overtimeHours > 0)
    ...(otApproval ? { otApproval } : {}),

    // Optional fields
    notes: notes || ''
  };

  const docRef = doc(db, 'companies', companyId, 'dailyTimeRecords', docId);

  await setDoc(
    docRef,
    {
      month: monthId,
      year: year,
      companyId,
      employeeId,
      employeeDocId,
      timeRecords: arrayUnion(newRecord),
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );

  return newRecord;
};

/**
 * Update an existing time record
 * @param {string} companyId - Company ID
 * @param {string} employeeId - Employee ID (4-digit)
 * @param {string} recordId - Record ID (e.g., "rec_20251102_001")
 * @param {object} updates - Updated record data
 * @returns {Promise<object>} Result with success status and updated record
 */
export const updateTimeRecord = async (companyId, employeeId, recordId, updates) => {
  // Extract date from recordId (rec_YYYYMMDD_###)
  const recordIdParts = recordId.split('_');
  if (recordIdParts.length !== 3 || recordIdParts[0] !== 'rec') {
    throw new Error('Invalid record ID format');
  }

  const dateStr = recordIdParts[1]; // "20251102"
  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const monthId = `${year}-${month}`;
  const docId = `${monthId}_${employeeId}`;

  // Get the document
  const docRef = doc(db, 'companies', companyId, 'dailyTimeRecords', docId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Time record document not found');
  }

  const data = docSnap.data();
  const timeRecords = data.timeRecords || [];

  // Find the record to update
  const recordIndex = timeRecords.findIndex(r => r.id === recordId);
  if (recordIndex === -1) {
    throw new Error('Time record not found in document');
  }

  const oldRecord = timeRecords[recordIndex];

  // Create updated record with audit trail
  const updatedRecord = {
    ...oldRecord,
    ...updates,
    updatedBy: updates.updatedBy || auth.currentUser?.uid || 'system',
    updatedAt: Timestamp.now()
  };

  // Remove otApproval field entirely when OT = 0 (null means "remove")
  if (updatedRecord.otApproval === null) {
    delete updatedRecord.otApproval;
  }

  // Update using arrayRemove + arrayUnion (atomic operation)
  // First remove the old record
  await setDoc(
    docRef,
    {
      timeRecords: arrayRemove(oldRecord)
    },
    { merge: true }
  );

  // Then add the updated record
  await setDoc(
    docRef,
    {
      timeRecords: arrayUnion(updatedRecord),
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );

  return { success: true, record: updatedRecord };
};

/**
 * Delete a time record
 * @param {string} companyId - Company ID
 * @param {string} employeeId - Employee ID (4-digit)
 * @param {string} recordId - Record ID (e.g., "rec_20251102_001")
 * @returns {Promise<object>} Result with success status
 */
export const deleteTimeRecord = async (companyId, employeeId, recordId) => {
  // Extract date from recordId (rec_YYYYMMDD_###)
  const recordIdParts = recordId.split('_');
  if (recordIdParts.length !== 3 || recordIdParts[0] !== 'rec') {
    throw new Error('Invalid record ID format');
  }

  const dateStr = recordIdParts[1]; // "20251102"
  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const monthId = `${year}-${month}`;
  const docId = `${monthId}_${employeeId}`;

  // Get the document
  const docRef = doc(db, 'companies', companyId, 'dailyTimeRecords', docId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Time record document not found');
  }

  const data = docSnap.data();
  const timeRecords = data.timeRecords || [];

  // Find the record to delete
  const record = timeRecords.find(r => r.id === recordId);
  if (!record) {
    throw new Error('Time record not found in document');
  }

  // Delete using arrayRemove
  await setDoc(
    docRef,
    {
      timeRecords: arrayRemove(record),
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );

  return { success: true };
};

/**
 * Check if a date is a holiday
 * @param {string} companyId - Company ID
 * @param {Date} date - Date to check
 * @returns {Promise<object|null>} Holiday data or null
 */
export const checkHoliday = async (companyId, date) => {
  try {
    const year = date.getFullYear();
    const dateString = formatDateString(date);

    const holidaysRef = collection(db, 'companies', companyId, 'holidays', String(year), 'items');
    const q = query(holidaysRef, where('date', '==', dateString));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      return snapshot.docs[0].data();
    }

    return null;
  } catch (error) {
    console.error('Error checking holiday:', error);
    return null;
  }
};

/**
 * Get active employees for a specific date
 * @param {string} companyId - Company ID
 * @param {Date} selectedDate - Date to check employee status
 * @returns {Promise<Array>} Array of active employees
 */
export const getActiveEmployees = async (companyId, selectedDate) => {
  try {
    const employeesRef = collection(db, 'companies', companyId, 'employees');
    const q = query(employeesRef, where('role', '==', 'employee'));
    const snapshot = await getDocs(q);

    const employees = [];
    snapshot.forEach((doc) => {
      employees.push({ id: doc.id, ...doc.data() });
    });

    // Use shared filtering logic
    const filteredEmployees = filterEmployeesByDate(employees, selectedDate);

    // Sort by employee ID
    filteredEmployees.sort((a, b) => {
      const idA = a.employeeId || '';
      const idB = b.employeeId || '';
      return idA.localeCompare(idB);
    });

    return filteredEmployees;
  } catch (error) {
    console.error('Error fetching active employees:', error);
    return [];
  }
};

/**
 * Validate time record data before saving
 * @param {object} data - Time record data to validate
 * @returns {object} { isValid: boolean, errors: object }
 */
export const validateTimeRecord = (data) => {
  const errors = {};

  // Required fields
  if (!data.employeeId) {
    errors.employee = 'Please select an employee';
  }

  if (!data.timeIn) {
    errors.timeIn = 'Please enter time in';
  }

  // Time range validation
  if (data.timeIn && data.timeOut) {
    if (data.timeOut <= data.timeIn) {
      errors.timeOut = 'Time out must be after time in';
    }

    // Check if time out is more than 24 hours after time in
    const hoursDiff = (data.timeOut - data.timeIn) / (1000 * 60 * 60);
    if (hoursDiff > 24) {
      errors.timeOut = 'Time out must be within 24 hours of time in';
    }
  }

  // Break validation
  if (data.breakMinutes && data.hoursWorked) {
    const breakHours = data.breakMinutes / 60;
    if (breakHours > data.hoursWorked) {
      errors.break = 'Break cannot exceed working hours';
    }
  }

  // Notes validation
  if (data.notes && data.notes.length > 100) {
    errors.notes = 'Notes cannot exceed 100 characters';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
